import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2, X, CheckCircle, AlertTriangle, FileText } from "lucide-react";

// Enhanced alert component with elegant, modern design from the first example
const CardAlert = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 5000); // Auto-dismiss after 5 seconds
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-md flex items-center justify-center z-50">
      <div
        className={`bg-white/95 rounded-3xl p-8 w-full max-w-md shadow-2xl`}
        style={{
          boxShadow:
            type === "success"
              ? "0 10px 25px -5px rgba(34, 197, 94, 0.25), 0 8px 10px -6px rgba(34, 197, 94, 0.1)"
              : "0 10px 25px -5px rgba(239, 68, 68, 0.25), 0 8px 10px -6px rgba(239, 68, 68, 0.1)",
          transform: "translateY(0px)",
          animation: "float 0.5s ease-out forwards",
        }}
      >
        <div className="flex items-center justify-center mb-6">
          <div
            className={`w-14 h-14 rounded-full flex items-center justify-center ${
              type === "success" ? "bg-green-100" : "bg-red-100"
            }`}
          >
            {type === "success" ? (
              <CheckCircle className="text-green-500 w-8 h-8" />
            ) : (
              <AlertTriangle className="text-red-500 w-8 h-8" />
            )}
          </div>
        </div>

        <h3
          className={`text-xl font-bold text-center mb-3 ${
            type === "success" ? "text-green-600" : "text-red-600"
          }`}
        >
          {type === "success" ? "Success" : "Error"}
        </h3>

        <p className="text-gray-700 text-center text-base mb-6">{message}</p>

        <div className="flex justify-center">
          <button
            onClick={onClose}
            className={`px-6 py-2 rounded-full text-white font-medium transition-all ${
              type === "success"
                ? "bg-green-500 hover:bg-green-600"
                : "bg-red-500 hover:bg-red-600"
            }`}
          >
            Dismiss
          </button>
        </div>

        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100"
          aria-label="Close alert"
        >
          <X size={20} />
        </button>
      </div>

      <style jsx>{`
        @keyframes float {
          0% {
            opacity: 0;
            transform: translateY(20px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

// ImportHistory component with functionality from the second example
const ImportHistory = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [alert, setAlert] = useState(null);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState(t("history.preparing"));

  useEffect(() => {
    // Simulate progress for better UX
    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 90) clearInterval(progressInterval);
        return Math.min(prev + 10, 90);
      });
    }, 200);

    const base64Data = searchParams.get("data");
    if (!base64Data) {
      setAlert({ message: t("history.importError"), type: "error" });
      setStatusMessage(t("history.errorOccurred"));
      setTimeout(() => navigate("/songs"), 5000);
      return () => clearInterval(progressInterval);
    }

    try {
      setTimeout(() => {
        setStatusMessage(t("history.decoding"));
      }, 600);

      setTimeout(() => {
        // Decode base64 and handle Unicode characters
        const latin1Data = atob(base64Data);
        const decodedData = decodeURIComponent(escape(latin1Data));
        const importedData = JSON.parse(decodedData);

        if (!Array.isArray(importedData)) {
          throw new Error("Invalid history format");
        }

        setStatusMessage(t("history.processing"));

        setTimeout(() => {
          const validData = importedData.filter(
            (item) => item.songID && item.title && item.viewedAt
          );

          const storedHistory = localStorage.getItem("songHistory");
          let mergedHistory = storedHistory ? JSON.parse(storedHistory) : [];

          validData.forEach((newItem) => {
            const existingIndex = mergedHistory.findIndex(
              (item) => item.songID === newItem.songID
            );
            if (existingIndex !== -1) {
              if (
                new Date(newItem.viewedAt) >
                new Date(mergedHistory[existingIndex].viewedAt)
              ) {
                mergedHistory[existingIndex] = newItem;
              }
            } else {
              mergedHistory.push(newItem);
            }
          });

          setStatusMessage(t("history.finalizing"));

          setTimeout(() => {
            const sortedHistory = mergedHistory
              .sort((a, b) => new Date(b.viewedAt) - new Date(a.viewedAt))
              .slice(0, 50);

            localStorage.setItem("songHistory", JSON.stringify(sortedHistory));
            setProgress(100);

            setAlert({
              message:
                t("history.importSuccess") + ` (${validData.length} items)`,
              type: "success",
            });

            // Redirect to the first song in the history (most recently viewed)
            const firstSong = sortedHistory[0];
            const redirectPath = firstSong
              ? `/songs/${firstSong.songID}`
              : "/songs";
            setTimeout(() => navigate(redirectPath), 5000);
          }, 600);
        }, 800);
      }, 1000);
    } catch (error) {
      console.error("Error importing shared history:", error);
      setAlert({ message: t("history.importError"), type: "error" });
      setStatusMessage(t("history.errorOccurred"));
      setProgress(100);
      setTimeout(() => navigate("/songs"), 5000);
    }

    return () => clearInterval(progressInterval);
  }, [searchParams, navigate, t]);

  return (
    <div className="flex justify-center items-center min-h-screen bg-white">
      <div className="bg-white rounded-3xl shadow-2xl p-10 w-full max-w-md mx-4 flex flex-col items-center">
        <div className="w-20 h-20 bg-orange-50 rounded-full flex items-center justify-center mb-6">
          <FileText className="w-10 h-10 text-orange-500" />
        </div>

        <h1 className="text-2xl font-bold text-gray-800 mb-2">
          {t("history.importingTitle")}
        </h1>

        <p className="text-gray-600 text-center mb-8">{statusMessage}</p>

        <div className="w-full bg-gray-100 rounded-full h-3 mb-6">
          <div
            className="bg-gradient-to-r from-orange-400 to-orange-500 h-3 rounded-full transition-all duration-300 ease-out"
            style={{ width: `${progress}%` }}
          ></div>
        </div>

        <div className="flex items-center justify-center mb-2">
          <Loader2
            className="w-8 h-8 text-orange-500 animate-spin mr-3"
            style={{ animationDuration: "1s" }}
          />
          <p className="text-gray-700 font-medium">{t("history.pleaseWait")}</p>
        </div>

        <p className="text-gray-500 text-sm text-center">
          {t("history.redirectSoon")}
        </p>
      </div>

      {alert && (
        <CardAlert
          message={alert.message}
          type={alert.type}
          onClose={() => setAlert(null)}
        />
      )}
    </div>
  );
};

export default ImportHistory;